import React from 'react';
import { Card, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PassOfferSummary } from '../../models/Pass';
import type { TutorSessionType } from '../../models/Tutor';
import { formatPassPrice, passEntitlementLabel } from '../../utils/passFormatting';
import { formatPassTargetSummary } from '../../utils/passTargets';
import TutorLessonSessionTypeChip from '../tutor/TutorLessonSessionTypeChip';

export interface PassOfferCardProps {
  offer: PassOfferSummary;
  sessionType?: TutorSessionType | null;
  /** Display names for the offer's target collection (order matches `targetIds`). */
  targetNames?: (string | null | undefined)[] | null;
  onBuy: (offer: PassOfferSummary) => void;
}

/**
 * Compact buy card: title + price on one row, then coverage, entitlement · validity, session chip.
 * Avoids the lesson-option two-column stack (price/entitlement/validity piled on the right).
 */
const PassOfferCard: React.FC<PassOfferCardProps> = ({ offer, sessionType, targetNames, onBuy }) => {
  const { t, i18n } = useTranslation();
  const targetSummary = formatPassTargetSummary(targetNames ?? []);
  const validityLabel =
    offer.validityStartPolicy === 'FROM_FIRST_USE'
      ? t('passes.validForDaysFromFirstUse', 'Valid for {{count}} days from first use', {
          count: offer.validityDurationDays,
        })
      : t('passes.validForDays', 'Valid for {{count}} days from purchase', {
          count: offer.validityDurationDays,
        });

  return (
    <Card
      variant="outlined"
      onClick={() => onBuy(offer)}
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        borderColor: 'divider',
        bgcolor: 'background.default',
        cursor: 'pointer',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: (theme) => theme.shadows[2],
        },
      }}
    >
      <Stack spacing={0.85}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.25, minWidth: 0 }}>
            {offer.title}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, flexShrink: 0, letterSpacing: '-0.01em' }}>
            {formatPassPrice(offer, i18n.language)}
          </Typography>
        </Stack>

        {targetSummary ? (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
            {t('passes.forTarget', 'For {{name}}', { name: targetSummary })}
          </Typography>
        ) : null}

        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
          {passEntitlementLabel(offer, t)}
          <Typography component="span" variant="body2" color="text.disabled" sx={{ mx: 0.75 }}>
            ·
          </Typography>
          {validityLabel}
        </Typography>

        <TutorLessonSessionTypeChip sessionType={sessionType} />
      </Stack>
    </Card>
  );
};

export default PassOfferCard;
